type TryCatchSuccess<T> = {
  /** The data returned from the promise. */
  data: T;
  /** The error returned from the promise. */
  error: null;
};

type TryCatchFailure<E> = {
  /** The error returned from the promise. */
  data: null;
  /** The error returned from the promise. */
  error: E;
};

/** The result object with discriminated union. */
type TryCatchResult<T, E = Error> = TryCatchSuccess<T> | TryCatchFailure<E>;

/**
 * Helper function to wrap a promise and return a result object with discriminated union.
 * @param promise - The promise to wrap.
 * @returns A result object with discriminated union.
 * @example
 * const {data, error} = await tryCatch(Promise.resolve("success"));
 *
 * if (error) { console.error(error); } // Handle error
 *
 * console.log(data); // "success"
 */
export const tryCatch = async <T, E = Error>(
  promise: Promise<T>
): Promise<TryCatchResult<T, E>> => {
  try {
    const data = await promise;
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error as E };
  }
};

/**
 * Helper function to wrap a callback and return a result object with discriminated union.
 * @param callback - The callback to wrap.
 * @returns A result object with discriminated union.
 * @example
 * const {data, error} = tryCatchNonAsync(()=> "success");
 *
 * if (error) { console.error(error); } // Handle error
 *
 * console.log(data); // "success"
 */
export const tryCatchNonAsync = <T, E = Error>(
  callback: () => T
): TryCatchResult<T, E> => {
  try {
    const data = callback();
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error as E };
  }
};
